import React, { useState } from 'react';
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, Legend } from 'recharts';


const segments = [
  { name: 'Power Users', count: 284, pct: 23, avgForms: 18, retention: 94, color: '#107570' },
  { name: 'Regular Users', count: 512, pct: 41, avgForms: 6, retention: 72, color: '#0d9488' },
  { name: 'Occasional', count: 318, pct: 26, avgForms: 2, retention: 48, color: '#14b8a6' },
  { name: 'At Risk', count: 124, pct: 10, avgForms: 0.4, retention: 21, color: '#f59e0b' },
];

const growthData = [
  { month: 'Jan', new: 142, active: 890, churned: 38 },
  { month: 'Feb', new: 168, active: 924, churned: 34 },
  { month: 'Mar', new: 195, active: 977, churned: 42 },
  { month: 'Apr', new: 221, active: 1056, churned: 31 },
  { month: 'May', new: 187, active: 1098, churned: 28 },
  { month: 'Jun', new: 243, active: 1184, churned: 24 },
  { month: 'Jul', new: 258, active: 1238, churned: 21 },
];

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload?.length) {
    return (
      <div className="bg-popover border border-border rounded-lg p-3 shadow-lg text-xs">
        <p className="font-semibold text-foreground mb-1">{label}</p>
        {payload?.map(p => (
          <p key={p?.dataKey} style={{ color: p?.color }}>{p?.name}: {p?.value?.toLocaleString()}</p>
        ))}
      </div>
    );
  }
  return null;
};

const UserSegments = () => {
  const [view, setView] = useState('segments');

  return (
    <div className="bg-card border border-border rounded-xl p-5">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h3 className="text-sm font-semibold text-foreground">User Segments & Growth</h3>
          <p className="text-xs text-muted-foreground mt-0.5">Behavioral segmentation and user base growth</p>
        </div>
        <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
          {['segments', 'growth']?.map(v => (
            <button
              key={v}
              onClick={() => setView(v)}
              className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${
                view === v ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {v === 'segments' ? 'Segments' : 'Growth'}
            </button>
          ))}
        </div>
      </div>
      {view === 'segments' ? (
        <div className="space-y-3">
          {segments?.map(s => (
            <div key={s?.name} className="flex items-center gap-4 p-3 rounded-lg bg-muted/40 hover:bg-muted/60 transition-colors">
              <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: s?.color }} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-semibold text-foreground">{s?.name}</span>
                  <span className="text-xs text-muted-foreground">{s?.pct}% of users</span>
                </div>
                <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${s?.pct * 2.5}%`, backgroundColor: s?.color }} />
                </div>
              </div>
              <div className="flex gap-4 shrink-0">
                <div className="text-center">
                  <p className="text-xs font-bold text-foreground">{s?.count?.toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground">users</p>
                </div>
                <div className="text-center">
                  <p className="text-xs font-bold text-foreground">{s?.avgForms}</p>
                  <p className="text-xs text-muted-foreground">avg forms</p>
                </div>
                <div className="text-center">
                  <p className="text-xs font-bold" style={{ color: s?.retention >= 70 ? '#107570' : s?.retention >= 40 ? '#f59e0b' : '#ef4444' }}>
                    {s?.retention}%
                  </p>
                  <p className="text-xs text-muted-foreground">retention</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={240}>
          <LineChart data={growthData} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis dataKey="month" tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }} />
            <YAxis tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }} />
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Line type="monotone" dataKey="active" name="Active Users" stroke="#107570" strokeWidth={2.5} dot={false} />
            <Line type="monotone" dataKey="new" name="New Users" stroke="#14b8a6" strokeWidth={2} dot={false} strokeDasharray="5 3" />
            <Line type="monotone" dataKey="churned" name="Churned" stroke="#ef4444" strokeWidth={1.5} dot={false} strokeDasharray="3 3" />
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

export default UserSegments;
